// @ts-nocheck
/**
 * MaterialListScreen - 素材一覧画面
 *
 * 全素材をカテゴリ別に一覧表示し、ティアフィルター・検索機能を提供
 * データは materialTypes.js から動的に取得（ハードコードなし）
 */

import React, { useMemo } from 'react';
import { useCardDex } from '../../hooks/useCardDex';
import { useCardData } from '../CardDexScreen';
import { useIsMobile } from '../../../hooks/useMediaQuery';
import {
  ATTRIBUTE_MATERIALS,
  CATEGORY_MATERIALS,
  GENERIC_MATERIALS,
  EXP_ORB_MATERIALS,
  RARITY_FRAGMENTS,
} from '../../../../twinKey/content/alchemy/data/materialTypes';
import { COLORS, gridStyles, filterStyles, navStyles } from '../../styles/cardDexStyles';

// ========================================
// 定数定義
// ========================================

// サブタブ定義（動的にデータを取得）
const MATERIAL_TABS = [
  { id: 'attribute', label: '属性', icon: '🔮', data: ATTRIBUTE_MATERIALS },
  { id: 'category', label: 'カテゴリ', icon: '🏷️', data: CATEGORY_MATERIALS },
  { id: 'generic', label: '汎用', icon: '💠', data: GENERIC_MATERIALS },
  { id: 'orbs', label: 'オーブ', icon: '🔵', data: EXP_ORB_MATERIALS },
  { id: 'fragments', label: '破片', icon: '✨', data: RARITY_FRAGMENTS },
];

// ティア日本語名マッピング
const TIER_NAMES = {
  common: 'コモン',
  uncommon: 'アンコモン',
  rare: 'レア',
  epic: 'エピック',
  legendary: 'レジェンダリー',
};

// ティアフィルターオプション
const TIER_OPTIONS = [
  { id: 'all', label: '全て' },
  { id: 'common', label: 'コモン', color: '#9e9e9e' },
  { id: 'uncommon', label: 'アンコモン', color: '#4caf50' },
  { id: 'rare', label: 'レア', color: '#2196f3' },
  { id: 'epic', label: 'エピック', color: '#9c27b0' },
  { id: 'legendary', label: 'レジェンダリー', color: '#ff9800' },
];

// ティアカラーマッピング
const TIER_COLORS = {
  common: '#9e9e9e',
  uncommon: '#4caf50',
  rare: '#2196f3',
  epic: '#9c27b0',
  legendary: '#ff9800',
};

// ========================================
// コンポーネント
// ========================================

const MaterialListScreen = () => {
  const {
    navigateToMaterialDetail,
    // 素材図鑑の状態（詳細から戻ったときに復元される）
    materialActiveTab: activeTab,
    materialTierFilter: tierFilter,
    materialSearchQuery: searchQuery,
    setMaterialActiveTab: setActiveTab,
    setMaterialTierFilter: setTierFilter,
    setMaterialSearchQuery: setSearchQuery,
  } = useCardDex();
  const { playerData } = useCardData();
  const isMobile = useIsMobile();
  const materials = playerData?.twinKeyData?.materials || {};

  // 現在のタブの素材をフィルタリング
  const filteredMaterials = useMemo(() => {
    const tabData = MATERIAL_TABS.find((t) => t.id === activeTab)?.data || {};
    let items = Object.values(tabData);

    // ティアフィルター
    if (tierFilter !== 'all') {
      items = items.filter((m) => m.tier === tierFilter);
    }

    // 検索フィルター
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      items = items.filter(
        (m) =>
          m.name.toLowerCase().includes(query) ||
          (m.description && m.description.toLowerCase().includes(query))
      );
    }

    return items;
  }, [activeTab, tierFilter, searchQuery]);

  // 合計素材数
  const totalMaterialCount = useMemo(() => {
    return MATERIAL_TABS.reduce((sum, tab) => {
      return sum + Object.keys(tab.data).length;
    }, 0);
  }, []);

  // 所持素材数
  const ownedMaterialCount = useMemo(() => {
    return Object.keys(materials).filter((id) => materials[id] > 0).length;
  }, [materials]);

  // スタイル
  const styles = {
    container: {
      padding: isMobile ? '8px' : '16px',
    },
    header: {
      marginBottom: '16px',
    },
    title: {
      fontSize: isMobile ? '18px' : '22px',
      fontWeight: 'bold',
      color: COLORS.textGold,
      marginBottom: '8px',
    },
    stats: {
      fontSize: '13px',
      color: COLORS.textSecondary,
    },
    tabContainer: {
      display: 'flex',
      gap: '6px',
      marginBottom: '12px',
      flexWrap: 'wrap',
    },
    tab: {
      padding: isMobile ? '6px 10px' : '8px 14px',
      border: 'none',
      borderRadius: '8px',
      background: 'rgba(30, 30, 50, 0.8)',
      color: COLORS.textSecondary,
      cursor: 'pointer',
      fontSize: isMobile ? '11px' : '13px',
      fontWeight: 'bold',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    tabActive: {
      background: COLORS.bgSelected,
      color: COLORS.textPrimary,
      border: `2px solid ${COLORS.borderHighlight}`,
    },
    filterRow: {
      display: 'flex',
      gap: '8px',
      marginBottom: '12px',
      flexWrap: 'wrap',
      alignItems: 'center',
    },
    tierButton: {
      padding: '4px 10px',
      border: `1px solid ${COLORS.borderSecondary}`,
      borderRadius: '6px',
      background: 'rgba(30, 30, 50, 0.6)',
      color: COLORS.textSecondary,
      cursor: 'pointer',
      fontSize: '11px',
      transition: 'all 0.2s ease',
    },
    tierButtonActive: {
      background: COLORS.bgSelected,
      border: `1px solid ${COLORS.borderHighlight}`,
      color: COLORS.textPrimary,
    },
    searchInput: {
      flex: 1,
      minWidth: '150px',
      maxWidth: '300px',
      padding: '8px 12px',
      border: `1px solid ${COLORS.borderSecondary}`,
      borderRadius: '8px',
      background: 'rgba(30, 30, 50, 0.6)',
      color: COLORS.textPrimary,
      fontSize: '13px',
      outline: 'none',
    },
    grid: {
      display: 'grid',
      gap: isMobile ? '8px' : '12px',
      gridTemplateColumns: isMobile
        ? 'repeat(auto-fill, minmax(90px, 1fr))'
        : 'repeat(auto-fill, minmax(120px, 1fr))',
    },
    materialCard: {
      background: COLORS.bgCard,
      borderRadius: '10px',
      padding: isMobile ? '8px' : '12px',
      cursor: 'pointer',
      border: '2px solid transparent',
      transition: 'all 0.2s ease',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '6px',
      position: 'relative',
    },
    materialCardHover: {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(107, 76, 230, 0.3)',
      border: `2px solid ${COLORS.borderPrimary}`,
    },
    materialIcon: {
      fontSize: isMobile ? '28px' : '36px',
      marginBottom: '4px',
    },
    materialName: {
      fontSize: isMobile ? '10px' : '12px',
      color: COLORS.textPrimary,
      textAlign: 'center',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      width: '100%',
    },
    tierBadge: {
      fontSize: '9px',
      padding: '2px 6px',
      borderRadius: '4px',
      fontWeight: 'bold',
    },
    ownedCount: {
      fontSize: '10px',
      color: COLORS.textSecondary,
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px 20px',
      color: COLORS.textSecondary,
    },
  };

  return (
    <div style={styles.container}>
      {/* ヘッダー */}
      <div style={styles.header}>
        <div style={styles.title}>🧪 素材図鑑</div>
        <div style={styles.stats}>
          収集: {ownedMaterialCount} / {totalMaterialCount}種
        </div>
      </div>

      {/* カテゴリタブ */}
      <div style={styles.tabContainer}>
        {MATERIAL_TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              ...styles.tab,
              ...(activeTab === tab.id ? styles.tabActive : {}),
            }}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
            <span style={{ opacity: 0.6 }}>({Object.keys(tab.data).length})</span>
          </button>
        ))}
      </div>

      {/* フィルター行 */}
      <div style={styles.filterRow}>
        {/* ティアフィルター */}
        {TIER_OPTIONS.map((tier) => (
          <button
            key={tier.id}
            onClick={() => setTierFilter(tier.id)}
            style={{
              ...styles.tierButton,
              ...(tierFilter === tier.id ? styles.tierButtonActive : {}),
              ...(tier.color && tierFilter === tier.id
                ? { border: `1px solid ${tier.color}`, color: tier.color }
                : {}),
            }}
          >
            {tier.label}
          </button>
        ))}

        {/* 検索 */}
        <input
          type="text"
          placeholder="素材を検索..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      {/* 素材グリッド */}
      {filteredMaterials.length > 0 ? (
        <div style={styles.grid}>
          {filteredMaterials.map((material) => {
            const ownedCount = materials[material.id] || 0;
            const tierColor = TIER_COLORS[material.tier] || TIER_COLORS.common;

            return (
              <div
                key={material.id}
                onClick={() => navigateToMaterialDetail(material.id)}
                style={styles.materialCard}
                onMouseEnter={(e) => {
                  Object.assign(e.currentTarget.style, styles.materialCardHover);
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = '';
                  e.currentTarget.style.boxShadow = '';
                  e.currentTarget.style.border = '2px solid transparent';
                }}
              >
                {/* アイコン */}
                <div style={styles.materialIcon}>{material.icon || '📦'}</div>

                {/* 名前 */}
                <div style={styles.materialName}>{material.name}</div>

                {/* ティアバッジ */}
                <div
                  style={{
                    ...styles.tierBadge,
                    background: `${tierColor}33`,
                    color: tierColor,
                    border: `1px solid ${tierColor}`,
                  }}
                >
                  {TIER_NAMES[material.tier] || material.tier}
                </div>

                {/* 所持数 */}
                <div style={styles.ownedCount}>
                  所持: {ownedCount > 0 ? ownedCount : '-'}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={styles.emptyState}>
          {searchQuery
            ? `"${searchQuery}" に一致する素材はありません`
            : '該当する素材がありません'}
        </div>
      )}
    </div>
  );
};

export default MaterialListScreen;
